// SPDX-License-Identifier: AGPL-3.0-or-later

import ExperimentAssignments from '@app/features/experiment/state/ExperimentAssignments';
import type {VoiceParticipantAvatarEntry} from '@app/features/voice/components/VoiceParticipantAvatarList';
import {buildVoiceParticipantIdentity} from '@app/features/voice/utils/VoiceParticipantIdentity';
import {sendEmote} from '@app/features/voice_scene/commands/RoomSignalCommands';
import {useCharacterRefreshFallback} from '@app/features/voice_scene/hooks/useCharacterRefreshFallback';
import type {
	RoomAvatarAnchors,
	RoomAvatarPerson,
	RoomAvatarPlace,
	RoomAvatars,
	RoomCharacterLook,
	RoomEmote,
	Vec3,
	VoiceSceneHandle,
} from '@app/features/voice_scene/scene/VoiceSceneTypes';
import RoomAvatarSignals from '@app/features/voice_scene/state/RoomAvatarSignals';
import {roomCharacterPreview} from '@app/features/voice_scene/state/RoomCharacterPreview';
import VoiceSceneCamera from '@app/features/voice_scene/state/VoiceSceneCamera';
import VoiceSceneSettings from '@app/features/voice_scene/state/VoiceSceneSettings';
import type {SeatAssignment, SeatParticipant} from '@app/features/voice_scene/utils/SeatAssignment';
import {stageHostSpot} from '@app/features/voice_scene/utils/StageHostSpot';
import {getWebglSupport} from '@app/features/voice_scene/utils/WebglSupport';
import {
	defaultShirtColor,
	formatCharacterAttribute,
	parseCharacterAttribute,
	ROOM_CHARACTER_ATTRIBUTE,
} from '@fluxer/constants/src/RoomCharacters';
import {parseOutfitAttribute, ROOM_OUTFIT_ATTRIBUTE, resolveOutfit} from '@fluxer/constants/src/RoomOutfit';
import {VOICE_ROOM_CHARACTER_PART_MANIFEST as PARTS} from '@fluxer/constants/src/VoiceRoomCharacterPartManifest';
import {VOICE_ROOM_STAGE_HOST_IDENTITY_PREFIX} from '@fluxer/constants/src/VoiceRoomStage';
import {readVoiceRoomAssignment} from '@fluxer/schema/src/domains/experiment/ExperimentSchemas';
import type {VoiceRoomStage} from '@fluxer/schema/src/domains/voice_seat/VoiceRoomStageSchemas';
import {type Participant, type Room, RoomEvent} from 'livekit-client';
import {useEffect, useMemo, useRef, useState} from 'react';

// How long an emoji floats over its sender, and how long the pet's purr bubble shows.
export const ROOM_FLOAT_MS = 2000;
export const ROOM_PURR_MS = 2400;
// A speaker's level may arrive after the speaking flag; above 0 is what keeps the mouth moving.
const MIN_SPEAKING_LEVEL = 0.01;
// Stand points fill up in turns; later turns stand beside the first one (metres along x, right then left).
const STAND_TURN_SHIFT_M = [0, 0.75, -0.75];

export interface RoomFloatEmote {
	key: number;
	emote: Extract<RoomEmote, {kind: 'emoji'} | {kind: 'custom'}>;
	until: number;
}

export interface RoomAvatarView {
	// The renderer's character layer (one per renderer, owned by this hook).
	layer: RoomAvatars;
	// identity → where the name tag and emotes sit; only people drawn as characters (loaded, within the cap).
	anchors: ReadonlyMap<string, RoomAvatarAnchors>;
	// identity → the emoji floating over that person now.
	emotes: ReadonlyMap<string, RoomFloatEmote>;
	// pet id → until when its purr shows.
	purrs: ReadonlyMap<string, number>;
}

type AvatarPerson = SeatParticipant & {isLocal: boolean};

// The two attributes the API writes on everyone in the room.
interface Worn {
	character: string | undefined;
	outfit: string | undefined;
}

const NO_WORN: ReadonlyMap<string, Worn> = new Map();
const NO_ANCHORS: ReadonlyMap<string, RoomAvatarAnchors> = new Map();
const NO_FLOATS = {emotes: new Map<string, RoomFloatEmote>(), purrs: new Map<string, number>()};

// Characters draw only with the room's avatars on, the local classic view off and WebGL there.
export function isRoomAvatarMode(): boolean {
	return (
		readVoiceRoomAssignment(ExperimentAssignments.response).avatars &&
		!VoiceSceneSettings.classicView &&
		getWebglSupport().webgl2
	);
}

// The server writes `micup.character` and `micup.outfit`: worn parts (contract rev 2.1) and a preset key in
// micup.character draw a resolved outfit. A missing, malformed or unknown value (a character this client no longer
// draws, such as the dropped GLM demo set) draws the default outfit in the person's default shirt.
function lookOf({character, outfit}: Partial<Worn>, userId: string): RoomCharacterLook {
	const parsed = parseCharacterAttribute(character);
	const parts = parseOutfitAttribute(outfit);
	const preset = parsed && Object.hasOwn(PARTS.presets, parsed.key) ? parsed.key : null;
	const resolved = resolveOutfit(PARTS, parts ?? [], preset);
	const shirt = parsed && (parts || preset) ? parsed.shirt : defaultShirtColor(userId);
	return {key: resolved.preset, shirt, outfit: resolved};
}

function readCharacters(room: Room): ReadonlyMap<string, Worn> {
	const characters = new Map<string, Worn>();
	for (const participant of [room.localParticipant, ...room.remoteParticipants.values()]) {
		const character = participant.attributes[ROOM_CHARACTER_ATTRIBUTE];
		const outfit = participant.attributes[ROOM_OUTFIT_ATTRIBUTE];
		if (character || outfit) characters.set(participant.identity, {character, outfit});
	}
	return characters;
}

function sameWorn(a: ReadonlyMap<string, Worn>, b: ReadonlyMap<string, Worn>): boolean {
	if (a.size !== b.size) return false;
	for (const [key, value] of a) {
		const other = b.get(key);
		if (!other || other.character !== value.character || other.outfit !== value.outfit) return false;
	}
	return true;
}

const samePoint = (a: Vec3, b: Vec3): boolean => a.x === b.x && a.y === b.y && a.z === b.z;

// The room's 3D characters: mounts the renderer's character layer while avatar mode is on, hands it who sits and
// stands where (look from `micup.character`), who speaks, and the filtered emotes and head directions; disposes it
// when the mode goes off or the room closes. null while off: the room draws today's avatars. stage: a bot's room stage
// with a host stands its character by the TV (StageHostSpot), last in the list so a full room draws its people first;
// a bot that is in the call itself is drawn once, as that participant.
export function useRoomAvatars({
	handle,
	room,
	channelId,
	connectionId,
	assignment,
	entries,
	stage = null,
}: {
	handle: VoiceSceneHandle;
	room: Room | null;
	channelId: string;
	connectionId: string | null;
	assignment: SeatAssignment<AvatarPerson>;
	entries: ReadonlyArray<VoiceParticipantAvatarEntry>;
	stage?: VoiceRoomStage | null;
}): RoomAvatarView | null {
	const enabled = isRoomAvatarMode();
	const [layer, setLayer] = useState<RoomAvatars | null>(null);
	useEffect(() => {
		if (!enabled) return;
		const mounted = handle.mountAvatars?.() ?? null;
		setLayer(mounted);
		return () => {
			mounted?.dispose();
			setLayer(null);
		};
	}, [handle, enabled]);

	const [characters, setCharacters] = useState(NO_WORN);
	useEffect(() => {
		if (!room || !layer) {
			setCharacters(NO_WORN);
			return;
		}
		const read = () => {
			const next = readCharacters(room);
			setCharacters((previous) => (sameWorn(previous, next) ? previous : next));
		};
		read();
		room.on(RoomEvent.ParticipantAttributesChanged, read);
		room.on(RoomEvent.ParticipantConnected, read);
		room.on(RoomEvent.ParticipantDisconnected, read);
		return () => {
			room.off(RoomEvent.ParticipantAttributesChanged, read);
			room.off(RoomEvent.ParticipantConnected, read);
			room.off(RoomEvent.ParticipantDisconnected, read);
		};
	}, [room, layer]);

	// Preview builds only: the picked candidate set redraws everyone (null in production).
	const previewSet = roomCharacterPreview?.set;
	// Preview builds only: the characters' light switch (production keeps them unlit).
	const previewLit = roomCharacterPreview?.lit ?? false;
	useEffect(() => {
		layer?.setLit?.(previewLit);
	}, [layer, previewLit]);
	// A board or score update keeps the host as it is: only the bot and its look redraw it.
	const hostBotId = stage?.host && !entries.some((entry) => entry.userId === stage.bot.id) ? stage.bot.id : null;
	const hostCharacter = stage?.host ? formatCharacterAttribute(stage.host.character, stage.host.shirt) : null;
	const people = useMemo(() => {
		const people: Array<RoomAvatarPerson> = [];
		if (!layer) return people;
		const add = (person: AvatarPerson, place: RoomAvatarPlace) => {
			const identity = buildVoiceParticipantIdentity(person.userId, person.connectionId);
			const look = lookOf(characters.get(identity) ?? {}, person.userId);
			people.push({
				identity,
				userId: person.userId,
				isLocal: person.isLocal,
				place,
				look: roomCharacterPreview ? roomCharacterPreview.lookFor(look, person.userId) : look,
			});
		};
		const seats = new Map(handle.layout.seats.map((seat) => [seat.name, seat]));
		for (const [name, person] of assignment.seated) {
			const seat = seats.get(name);
			if (seat) add(person, {kind: 'seat', seat});
		}
		const turn = Math.max(handle.layout.stands.length, 1);
		assignment.standing.forEach((person, index) => {
			const point = assignment.placements.get(person.userId)?.position;
			if (!point) return;
			const shift = STAND_TURN_SHIFT_M[Math.floor(index / turn)] ?? 0;
			add(person, {kind: 'stand', point: shift ? {...point, x: point.x + shift} : point});
		});
		if (hostBotId && hostCharacter)
			people.push({
				identity: VOICE_ROOM_STAGE_HOST_IDENTITY_PREFIX + hostBotId,
				userId: hostBotId,
				isLocal: false,
				place: {kind: 'stand', point: stageHostSpot(handle.layout)},
				look: lookOf({character: hostCharacter}, hostBotId),
			});
		return people;
	}, [layer, handle, assignment, characters, previewSet, hostBotId, hostCharacter]);

	// anchors() answers only for a loaded character within the cap; the renderer fires a view change when one loads,
	// moves or leaves, so the name tags are read again then (kept as they are while nothing changed).
	const [anchors, setAnchors] = useState(NO_ANCHORS);
	const shownAnchors = useRef(NO_ANCHORS);
	useEffect(() => {
		if (!layer) {
			shownAnchors.current = NO_ANCHORS;
			setAnchors(NO_ANCHORS);
			return;
		}
		layer.setPeople(people);
		const read = () => {
			const current = shownAnchors.current;
			let shown = 0;
			let same = true;
			for (const {identity} of people) {
				const next = layer.anchors(identity);
				if (!next) continue;
				shown++;
				const before = current.get(identity);
				if (!before || !samePoint(before.nametag, next.nametag) || !samePoint(before.emote, next.emote)) {
					same = false;
					break;
				}
			}
			if (same && shown === current.size) return;
			const map = new Map<string, RoomAvatarAnchors>();
			for (const {identity} of people) {
				const next = layer.anchors(identity);
				if (next) map.set(identity, next);
			}
			shownAnchors.current = map;
			setAnchors(map);
		};
		read();
		return handle.onViewChange(read);
	}, [layer, handle, people]);

	const speakingKey = entries
		.flatMap((entry) => (entry.speaking ? [buildVoiceParticipantIdentity(entry.userId, entry.connectionId)] : []))
		.join('|');
	const speaking = useRef<{layer: RoomAvatars | null; ids: ReadonlySet<string>}>({layer: null, ids: new Set()});
	useEffect(() => {
		if (!layer) return;
		const before = speaking.current.layer === layer ? speaking.current.ids : new Set<string>();
		const now = new Set(speakingKey ? speakingKey.split('|') : []);
		for (const identity of now)
			if (!before.has(identity))
				layer.setSpeaking(
					identity,
					Math.max(room?.getParticipantByIdentity(identity)?.audioLevel ?? 0, MIN_SPEAKING_LEVEL),
				);
		for (const identity of before) if (!now.has(identity)) layer.setSpeaking(identity, 0);
		speaking.current = {layer, ids: now};
	}, [layer, room, speakingKey]);
	useEffect(() => {
		if (!layer || !room) return;
		const onSpeakers = (speakers: Array<Participant>) => {
			const {ids} = speaking.current;
			for (const speaker of speakers)
				if (ids.has(speaker.identity))
					layer.setSpeaking(speaker.identity, Math.max(speaker.audioLevel, MIN_SPEAKING_LEVEL));
		};
		room.on(RoomEvent.ActiveSpeakersChanged, onSpeakers);
		return () => {
			room.off(RoomEvent.ActiveSpeakersChanged, onSpeakers);
		};
	}, [layer, room]);

	// Emotes, head directions and pets from RoomAvatarSignals (already filtered); one sweep timer ends the floats.
	const [floats, setFloats] = useState(NO_FLOATS);
	useEffect(() => {
		if (!layer) return;
		const emotes = new Map<string, RoomFloatEmote>();
		const purrs = new Map<string, number>();
		let timer: ReturnType<typeof setTimeout> | null = null;
		let key = 0;
		const publish = () => setFloats({emotes: new Map(emotes), purrs: new Map(purrs)});
		const sweep = () => {
			timer = null;
			const now = Date.now();
			let next = Number.POSITIVE_INFINITY;
			for (const [identity, float] of emotes)
				if (float.until <= now) emotes.delete(identity);
				else next = Math.min(next, float.until);
			for (const [pet, until] of purrs)
				if (until <= now) purrs.delete(pet);
				else next = Math.min(next, until);
			publish();
			if (next !== Number.POSITIVE_INFINITY) timer = setTimeout(sweep, next - now);
		};
		const show = () => {
			publish();
			timer ??= setTimeout(sweep, ROOM_FLOAT_MS);
		};
		const stopSignals = RoomAvatarSignals.subscribe((signal) => {
			if ('head' in signal) {
				layer.setHead(signal.identity, signal.head.yawDeg, signal.head.pitchDeg);
				return;
			}
			const {identity, emote} = signal;
			if (emote.kind === 'pet') {
				handle.playPet?.(emote.pet);
				purrs.set(emote.pet, Date.now() + ROOM_PURR_MS);
				show();
				return;
			}
			layer.playEmote(identity, emote, signal.at);
			if (emote.kind !== 'emoji' && emote.kind !== 'custom') return;
			emotes.set(identity, {key: ++key, emote, until: Date.now() + ROOM_FLOAT_MS});
			show();
		});
		const stopPets = handle.onPet?.((pet) => {
			sendEmote({kind: 'pet', pet});
		});
		return () => {
			stopSignals();
			stopPets?.();
			if (timer !== null) clearTimeout(timer);
			setFloats(NO_FLOATS);
		};
	}, [layer, handle]);

	const myIdentity = room?.localParticipant.identity ?? null;
	useEffect(() => {
		if (!layer || !myIdentity) return;
		return VoiceSceneCamera.register(handle, myIdentity);
	}, [layer, handle, myIdentity]);
	useCharacterRefreshFallback({
		channelId,
		connectionId: layer ? connectionId : null,
		present: myIdentity !== null && Boolean(characters.get(myIdentity)?.character),
	});

	return useMemo(
		() => (layer ? {layer, anchors, emotes: floats.emotes, purrs: floats.purrs} : null),
		[layer, anchors, floats],
	);
}
